import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Shield, ShieldAlert, ShieldCheck, Trash2, Search, RefreshCw,
  Loader2, Ban, Unlock, AlertTriangle, Clock, Globe, User
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IPSecurityManager() {
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [threats, setThreats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blockIP, setBlockIP] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [blockPermanent, setBlockPermanent] = useState(true);
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupResult, setLookupResult] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await axios.get(`${API}/admin/ip-security/blocked`);
      setBlockedIPs(res.data.blocked_ips);
      setThreats(res.data.active_threats);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBlock = async () => {
    if (!blockIP.trim()) return toast.error('Enter an IP address');
    try {
      await axios.post(`${API}/admin/ip-security/block`, null, {
        params: { ip: blockIP, reason: blockReason || 'Manually blocked', permanent: blockPermanent }
      });
      toast.success(`IP ${blockIP} blocked`);
      setBlockIP('');
      setBlockReason('');
      loadData();
    } catch (err) {
      toast.error('Failed to block IP');
    }
  };

  const handleUnblock = async (ip) => {
    try {
      await axios.delete(`${API}/admin/ip-security/unblock/${ip}`);
      toast.success(`IP ${ip} unblocked`);
      loadData();
    } catch (err) {
      toast.error('Failed to unblock');
    }
  };

  const handleClearAttempts = async (ip) => {
    try {
      await axios.delete(`${API}/admin/ip-security/clear-attempts/${ip}`);
      toast.success('Attempts cleared');
      loadData();
    } catch (err) {
      toast.error('Failed to clear');
    }
  };

  const handleLookup = async () => {
    if (!lookupEmail.trim()) return;
    try {
      const res = await axios.get(`${API}/admin/ip-security/lookup/${lookupEmail}`);
      setLookupResult(res.data);
    } catch (err) {
      toast.error('Lookup failed');
    }
  };

  const handleUnlockAccount = async (email) => {
    try {
      await axios.delete(`${API}/admin/ip-security/unlock-account/${email}`);
      toast.success(`Account ${email} unlocked`);
      setLookupResult(null);
      setLookupEmail('');
    } catch (err) {
      toast.error('Failed to unlock');
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleString(); } catch { return d; }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Block IP */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Ban className="w-4 h-4 text-red-400" />
            Block IP Address
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-gray-400 text-xs">IP Address</Label>
              <Input value={blockIP} onChange={(e) => setBlockIP(e.target.value)} placeholder="192.168.1.1" className="bg-[#252540] border-[#2d2d44] text-white font-mono" />
            </div>
            <div className="space-y-1">
              <Label className="text-gray-400 text-xs">Reason</Label>
              <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="Suspicious activity" className="bg-[#252540] border-[#2d2d44] text-white" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Switch checked={blockPermanent} onCheckedChange={setBlockPermanent} />
              <span className="text-sm text-gray-400">{blockPermanent ? 'Permanent block' : 'Temporary (24h)'}</span>
            </div>
            <Button onClick={handleBlock} className="bg-red-600 hover:bg-red-700" size="sm">
              <Ban className="w-4 h-4 mr-1" /> Block
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Lookup & Unlock */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <User className="w-4 h-4 text-blue-400" />
            Account Lookup & Unlock
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={lookupEmail} onChange={(e) => setLookupEmail(e.target.value)} placeholder="user@mypatriotmail.org" className="bg-[#252540] border-[#2d2d44] text-white" onKeyDown={(e) => e.key === 'Enter' && handleLookup()} />
            <Button onClick={handleLookup} variant="outline" className="border-[#2d2d44] text-gray-400">
              <Search className="w-4 h-4" />
            </Button>
          </div>
          {lookupResult && (
            <div className="p-3 bg-[#252540] rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium">{lookupResult.email}</p>
                  <p className="text-xs text-gray-500">
                    Failed attempts: <span className="text-amber-400">{lookupResult.failed_attempts}</span>
                    {lookupResult.locked_until && (
                      <span className="text-red-400 ml-2">Locked until {formatDate(lookupResult.locked_until)}</span>
                    )}
                  </p>
                </div>
                {lookupResult.failed_attempts > 0 && (
                  <Button onClick={() => handleUnlockAccount(lookupResult.email)} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                    <Unlock className="w-4 h-4 mr-1" /> Unlock
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Blocked IPs List */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-red-400" />
              Blocked IPs
              {blockedIPs.length > 0 && <Badge className="bg-red-500/20 text-red-400 text-xs">{blockedIPs.length}</Badge>}
            </CardTitle>
            <Button onClick={loadData} variant="outline" size="icon" className="border-[#2d2d44] text-gray-400 h-8 w-8">
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {blockedIPs.length === 0 ? (
            <div className="text-center py-6">
              <ShieldCheck className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No blocked IPs</p>
            </div>
          ) : (
            <div className="space-y-2">
              {blockedIPs.map((b, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-[#252540] rounded-lg">
                  <div className="flex items-center gap-3">
                    <Globe className="w-4 h-4 text-red-400 flex-shrink-0" />
                    <div>
                      <code className="text-sm text-white font-mono">{b.ip}</code>
                      <p className="text-xs text-gray-500">{b.reason}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={b.permanent ? 'bg-red-500/20 text-red-400 text-xs' : 'bg-amber-500/20 text-amber-400 text-xs'}>
                          {b.permanent ? 'Permanent' : `Expires ${formatDate(b.expires_at)}`}
                        </Badge>
                        {b.blocked_by && <span className="text-xs text-gray-600">by {b.blocked_by}</span>}
                      </div>
                      {b.emails_attempted?.length > 0 && (
                        <p className="text-xs text-gray-600 mt-1">Tried: {b.emails_attempted.slice(0, 3).join(', ')}{b.emails_attempted.length > 3 ? '...' : ''}</p>
                      )}
                    </div>
                  </div>
                  <Button onClick={() => handleUnblock(b.ip)} variant="outline" size="sm" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                    <Unlock className="w-3 h-3 mr-1" /> Unblock
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Threats */}
      {threats.length > 0 && (
        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              Active Threats
              <Badge className="bg-amber-500/20 text-amber-400 text-xs">{threats.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {threats.map((t, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-[#252540] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${t.attempts >= 10 ? 'bg-red-500/20' : 'bg-amber-500/20'}`}>
                      <span className={`text-xs font-bold ${t.attempts >= 10 ? 'text-red-400' : 'text-amber-400'}`}>{t.attempts}</span>
                    </div>
                    <div>
                      <code className="text-sm text-white font-mono">{t.ip}</code>
                      <p className="text-xs text-gray-500">
                        Last: {formatDate(t.last_attempt)}
                        {t.emails?.length > 0 && ` · ${t.emails.slice(-2).join(', ')}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleClearAttempts(t.ip)} variant="outline" size="sm" className="border-[#2d2d44] text-gray-400 text-xs">
                      Clear
                    </Button>
                    <Button onClick={() => { setBlockIP(t.ip); setBlockReason(`Suspicious: ${t.attempts} failed attempts`); }} variant="outline" size="sm" className="border-red-500/30 text-red-400 text-xs">
                      <Ban className="w-3 h-3 mr-1" /> Block
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
